import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import {
    Grid, Card, TextField, Button, makeStyles, Typography,
    Box, Container, InputAdornment, CircularProgress
} from '@material-ui/core';
import { Link } from 'react-router-dom';
import MailIcon from '@material-ui/icons/Mail';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import registration from "../../assets/images/registration.svg";
import bgLogo from "../../assets/images/bg.svg";
import { forgotPassword } from '../../store/action';
import { useForm } from 'react-hook-form';
import Notify from '../../components/Notify/Notify';

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(3),
    position: "relative",
    overflow: "hidden",
    "&::before": {
      content: '""',
      position: "absolute",
      width: "200%",
      height: "200%",
      top: "-50%",
      left: "-50%",
      zIndex: 0,
      background: `url(${bgLogo})`,
      backgroundSize: "contain",
      opacity: 0.05,
      transform: "rotate(-15deg)",
    },
  },
  container: {
    position: "relative",
    zIndex: 1,
  },
  authCard: {
    background: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius * 2,
    overflow: "hidden",
    boxShadow: theme.shadows[8],
    // maxWidth: 1000,
    width: "100%",
    padding:0
  },
  leftPanel: {
    background: `linear-gradient(135deg, ${theme.palette.primary.light}20 0%, ${theme.palette.primary.main}20 100%)`,
    padding: theme.spacing(6),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(4),
      borderRight: "none",
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  rightPanel: {
    padding: theme.spacing(5),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(3),
    },
  },
  form: {
    width: "100%",
  },
  inputIcon: {
    color: theme.palette.primary.main,
  },
  button: {
    borderRadius: theme.shape.borderRadius,
    height: 48,
    fontWeight: 600,
    marginTop: theme.spacing(2),
    background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    boxShadow: theme.shadows[2],
    "&:hover": {
      boxShadow: theme.shadows[4],
      background: `linear-gradient(45deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
    },
  },
  backButton: {
    color: theme.palette.primary.main,
    fontWeight: 500,
    textDecoration: "none",
    display: "flex",
    alignItems: "center",
    marginTop: theme.spacing(2),
    "&:hover": {
      textDecoration: "underline",
    },
  },
  textField: {
    marginBottom: theme.spacing(2),
    "& .MuiOutlinedInput-root": {
      borderRadius: theme.shape.borderRadius,
      "&:hover fieldset": {
        borderColor: theme.palette.primary.light,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: 1,
      },
    },
  },
  illustration: {
    width: "100%",
    maxWidth: 280,
    height: "auto",
    marginBottom: theme.spacing(3),
  },
  welcomeText: {
    textAlign: "center",
    color: theme.palette.primary.main,
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    textAlign: "center",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  instructions: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3),
    fontSize: "0.9rem",
  },
}));

const ForgottenPassword = () => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { forgotsuccess, forgoterrors, loading } = useSelector(state => state.authenticate);
    const { register, handleSubmit, formState: { errors } } = useForm();
    const [forgotMsg, setForgotMsg] = useState(false);

    const onSubmit = (data) => {
        dispatch(forgotPassword(data));
        setForgotMsg(true);
    };

    return (
        <>
            {forgotMsg && (forgoterrors?.message || forgoterrors) && (
                <Notify
                    data={forgoterrors?.message || forgoterrors}
                    status="error"
                />
            )}
            {forgotMsg && forgotsuccess?.message && (
                <Notify
                    data={forgotsuccess?.message}
                    status="success"
                />
            )}
            
            <Box className={classes.root}>
                <Container maxWidth="lg" className={classes.container}>
                    <Card className={classes.authCard}>
                        <Grid container alignItems="center">
                            {/* Left Section - Illustration */}
                            <Grid item xs={12} md={6} className={classes.leftPanel}>
                                <img
                                    src={registration}
                                    alt="Password Recovery Illustration"
                                    className={classes.illustration}
                                />
                                <Typography variant="h5" className={classes.welcomeText} gutterBottom>
                                    Forgot Your Password?
                                </Typography>
                                <Typography variant="body1" className={classes.subtitle}>
                                    No worries, we'll help you reset it
                                </Typography>
                            </Grid>

                            {/* Right Section - Form */}
                            <Grid item xs={12} md={6} className={classes.rightPanel}>
                                <Typography variant="h5" gutterBottom color="primary">
                                    Password Recovery
                                </Typography>
                                <Typography variant="body2" className={classes.instructions}>
                                    Enter your email address and we'll send you instructions to reset your password.
                                </Typography>
                                
                                <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
                                    <TextField
                                        placeholder="Email Address"
                                        variant="outlined"
                                        className={classes.textField}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <MailIcon className={classes.inputIcon} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        {...register("email", {
                                            required: "Email is required",
                                            pattern: {
                                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                message: "Invalid email address",
                                            },
                                        })}
                                        error={!!errors.email}
                                        helperText={errors.email?.message}
                                        fullWidth
                                        margin="normal"
                                    />

                                    <Button
                                        type="submit"
                                        fullWidth
                                        variant="contained"
                                        color="primary"
                                        className={classes.button}
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <CircularProgress size={24} color="inherit" />
                                        ) : (
                                            "Send Reset Instructions"
                                        )}
                                    </Button>

                                    <Link to="/login" className={classes.backButton}>
                                        <ArrowBackIcon style={{ marginRight: 8, fontSize: 18 }} />
                                        Back to Login
                                    </Link>
                                </form>
                            </Grid>
                        </Grid>
                    </Card>
                </Container>
            </Box>
        </>
    );
};

export default ForgottenPassword;